import { Granularity } from 'bindings/time/Granularity';
import * as React from 'react';
import { getTranslate, Translate } from 'react-localize-redux';
import { useRecoilValue } from 'recoil';
import { MappingResume } from '../web-types/mapping';
import { AFormTemplate } from '../../../atoms/forms';
import { AEventTypes, AtomEventType } from '../../../atoms/global/events';
import { AExternalMappings } from '../../../atoms/global/mappings';
import { creationPopupDropdownStyle } from '../../popup/style/CreationFormStyle';
import { LoadingStateEnum } from '../../../containers_v2/import/model';
import storeLang from '../../../helpers/storeLang';
import { DefaultButton } from '../../../styles/global/css/GlobalButton';
import { PrimaryColor } from '../../../styles/global/css/Utils';
import { translateToString } from '../../../styles/global/translate';
import Dropdown from '../../dropdown/Dropdown';
import { DropdownData } from '../../dropdown/model/Model';
import PopupCreation from '../../popup/PopupCreation';
import {
	FilterAdditionalValue,
	FilterId,
	isCalculatedFieldFilter,
	isCheckoutEvolutionFilter,
	isExternalIdFilter,
	isFrequencyFilter,
	isLastEventDateFilter,
	isLastFormDateFilter,
	isNextEventDateFilter,
	unwrapFilterId,
	wrapFilterId
} from '../model/Model';
import { AProducts, Product } from '../../../atoms/product';
import { FilterText } from '../style/AdvancedFilterStyle';
import { Filter } from './FilterList';

export function filterIdFormater(wrappedId: FilterId, additionalValue?: FilterAdditionalValue): FilterId {
	const id = unwrapFilterId(wrappedId);
	let res: FilterId;
	if (isFrequencyFilter(id) && (typeof additionalValue?.value === 'undefined' || typeof additionalValue?.value === 'number')) {
		res = { frequencies: { ...id.frequencies, event: id.frequencies.event ?? additionalValue?.value } };
	} else if (isLastEventDateFilter(id) && (typeof additionalValue?.value === 'undefined' || typeof additionalValue?.value === 'number')) {
		res = { last_event_date: id.last_event_date === null ? (additionalValue?.value ?? null) : null };
	} else if (isNextEventDateFilter(id) && (typeof additionalValue?.value === 'undefined' || typeof additionalValue?.value === 'number')) {
		res = { next_event_date: id.next_event_date === null ? (additionalValue?.value ?? null) : null };
	} else if (isExternalIdFilter(id) && (typeof additionalValue?.value === 'undefined' || typeof additionalValue?.value === 'number')) {
		res = { external_id: id.external_id === null ? (additionalValue?.value ?? null) : null };
	} else if (isCheckoutEvolutionFilter(id) && (typeof additionalValue?.value === 'undefined' || typeof additionalValue?.value === 'string')) {
		res = { checkout: { ...id.checkout, granularity: id.checkout.granularity ?? additionalValue?.value as Granularity } };
	} else if (isLastFormDateFilter(id) && (typeof additionalValue?.value === 'undefined' || typeof additionalValue?.value === 'number')) {
		res = { last_form_date: !id.last_form_date ? (additionalValue?.value ?? null) : null };
	} else if (isCalculatedFieldFilter(id) && typeof additionalValue?.value === 'string') {
		// per-product calculated field: the intermediate screen picked a product
		res = { calculated_field_columns: id.calculated_field_columns.map(c => ({ ...c, product: additionalValue.value as string })) };
	} else {
		res = id;
	}

	return wrapFilterId(res, wrappedId);
}

export default function FilterSelection(props: {
	content: JSX.Element,
	filter: Filter,
	loading: LoadingStateEnum,
	onClose?: () => void,
	buttonReturn: () => void,
	title?: string,
	noHeader?: boolean
	overflow?: string
}): JSX.Element {
	const { filter } = props;
	const unwrapedFilterId = unwrapFilterId(filter.id);
	const [selectedValue, setSelectedValue] = React.useState<number | string>();
	const [valueTitleComment, setValueTitleComment] = React.useState<string | JSX.Element>();
	const [step, setStep] = React.useState<boolean>(false);
	const eventTypes = useRecoilValue(AEventTypes);
	const externalMappings = useRecoilValue(AExternalMappings);
	const formTemplates = useRecoilValue(AFormTemplate).instanceForms;
	const products = useRecoilValue(AProducts);
	const translate = getTranslate(storeLang.getState().localize);
	let jsx: JSX.Element = React.cloneElement(
		props.content,
		{ additionalValue: {
			value: selectedValue,
			titleComment: valueTitleComment
		} }
	);

	if (((isFrequencyFilter(unwrapedFilterId) && unwrapedFilterId.frequencies.event === undefined)
		|| (isLastEventDateFilter(unwrapedFilterId) && unwrapedFilterId.last_event_date === null)
		|| (isNextEventDateFilter(unwrapedFilterId) && unwrapedFilterId.next_event_date === null)
	)
		&& !step) {
		jsx = <div style={{ padding: '0 1em' }}>
			<FilterText margin='0 0 5px 0' color={PrimaryColor} cursor="pointer" fontSize="11px" onClick={() => props.buttonReturn()}>&lt;<Translate id='back' /></FilterText>
			<Dropdown<AtomEventType>
				datalist={eventTypes.map(c => ({ value: c, label: translate(`event.${c.name}`).toString() }))}
				dropdownStyle={creationPopupDropdownStyle}
				name={''}
				onChange={(value: DropdownData<AtomEventType>) => {
					setSelectedValue(value.value.id);
					setValueTitleComment(value.label);
				}}
			/>
			<DefaultButton width="125px" margin='20px 0' disabled={!selectedValue} onClick={() => setStep(true)}><Translate id='continue' /></DefaultButton>
		</div>;
	} else if (isExternalIdFilter(unwrapedFilterId) && unwrapedFilterId.external_id === null && !step) {
		const filteredList = externalMappings.filter(em => em.mapping_type_id === 1);
		if (filteredList.length == 1) {
			setSelectedValue(filteredList[0].id);
			setValueTitleComment(filteredList[0].name);
			setStep(true);
		} else {
			jsx = <div style={{ padding: '0 1em' }}>
				<FilterText margin='0 0 5px 0' color={PrimaryColor} cursor="pointer" fontSize="11px" onClick={() => props.buttonReturn()}>&lt;<Translate id='back' /></FilterText>
				<Dropdown<MappingResume>
					datalist={filteredList.map(c => ({ value: c, label:c.name }))}
					dropdownStyle={creationPopupDropdownStyle}
					name={''}
					onChange={(value: DropdownData<MappingResume>) => {
						setSelectedValue(value.value.id);
						setValueTitleComment(value.label);
					}}
				/>
				<DefaultButton width="125px" margin='20px 0' disabled={!selectedValue} onClick={() => setStep(true)}><Translate id='continue' /></DefaultButton>
			</div>;
		}
	} else if (isCheckoutEvolutionFilter(unwrapedFilterId) && !unwrapedFilterId.checkout.granularity && !step) {
		jsx = <div style={{ padding: '0 1em' }}>
			<FilterText margin='0 0 5px 0' color={PrimaryColor} cursor="pointer" fontSize="11px" onClick={() => props.buttonReturn()}>&lt;<Translate id='back' /></FilterText>
			<Dropdown
				datalist={['month', 'quarter', 'year'].map(g => ({ label: translateToString(g), value: g }))}
				dropdownStyle={creationPopupDropdownStyle}
				name={''}
				onChange={value => {
					setSelectedValue(value.value);
					setValueTitleComment(value.label);
				}}
			/>
			<DefaultButton width="125px" margin='20px 0' disabled={!selectedValue} onClick={() => setStep(true)}><Translate id='continue' /></DefaultButton>
		</div>;
	} else if (isLastFormDateFilter(unwrapedFilterId) && !unwrapedFilterId.last_form_date && !step) {
		jsx = <div style={{ padding: '0 1em' }}>
			<FilterText margin='0 0 5px 0' color={PrimaryColor} cursor="pointer" fontSize="11px" onClick={() => props.buttonReturn()}>&lt;<Translate id='back' /></FilterText>
			<Dropdown
				datalist={formTemplates.map(g => ({ label: g.name, value: g }))}
				dropdownStyle={creationPopupDropdownStyle}
				name={''}
				onChange={value => {
					setSelectedValue(value.value.id);
					setValueTitleComment(value.label);
				}}
			/>
			<DefaultButton width="125px" margin='20px 0' disabled={!selectedValue} onClick={() => setStep(true)}><Translate id='continue' /></DefaultButton>
		</div>;
	} else if (filter.products && !step) {
		// per-product calculated field: pick the product whose value the filter applies to
		jsx = <div style={{ padding: '0 1em' }}>
			<FilterText margin='0 0 5px 0' color={PrimaryColor} cursor="pointer" fontSize="11px" onClick={() => props.buttonReturn()}>&lt;<Translate id='back' /></FilterText>
			<Dropdown<Product>
				datalist={[...products]
					.sort((a, b) => a.name.toLocaleLowerCase().localeCompare(b.name.toLocaleLowerCase()))
					.map(p => ({ value: p, label: p.name }))}
				dropdownStyle={creationPopupDropdownStyle}
				name={''}
				onChange={(value: DropdownData<Product>) => {
					setSelectedValue(value.value.uuid);
					setValueTitleComment(value.label);
				}}
			/>
			<DefaultButton width="125px" margin='20px 0' disabled={!selectedValue} onClick={() => setStep(true)}><Translate id='continue' /></DefaultButton>
		</div>;
	}

	return <PopupCreation
		noHeader={props.noHeader}
		title={props.title ?? translate('advanced_filters').toString()}
		content={jsx}
		loading={props.loading}
		onClose={props.onClose}
		hideValidation
		overflow={props.overflow}
	/>;
}
